import Button from 'Components/Atoms/Button/Button';
import OutlineAdd from 'Components/Atoms/OutlineIcons/OutlineAdd';
import useDrawerStack from 'Hooks/useDrawerStack';
import { useTranslation } from 'react-i18next';
import { DRAWER_STACK } from 'Utils/constants';
import { createReference, deleteReference, updateReference } from 'Services/references';
import Loading from 'Components/Atoms/Loading/Loading';
import { useQueryClient } from '@tanstack/react-query';
import IconButton from 'Components/Atoms/IconButton/IconButton';
import Icon from 'Components/Atoms/Icon/Icon';
import { useState } from 'react';
import PropTypes from 'prop-types';
import { cn } from 'Utils/cn';
import {
    useFloating,
    useClick,
    useDismiss,
    useRole,
    useInteractions,
    FloatingFocusManager,
    offset,
    flip,
    autoUpdate,
} from '@floating-ui/react';
import { AnimatePresence, motion } from 'framer-motion';
import useInfiniteReferences from 'Hooks/useInfiniteReferences';
import InfiniteScroll from 'Components/Molecules/InfiniteScroll/InfiniteScroll';
import useLocale from 'Hooks/useLocale';
import useCurrency from 'Hooks/useCurrency';
import { formatCurrency } from 'Utils/formatters';

function ReferenceRow({ reference, onEdit, onDelete, className }) {
    const { name, type, unit_price, unit_measurement } = reference;
    const [open, setOpen] = useState(false);

    const { t } = useTranslation('invoices');
    const { t: tRef } = useTranslation('references');

    const locale = useLocale();
    const currency = useCurrency();

    const typeLabel = tRef(`fields-type-${type}`);
    const priceLabel =
        unit_price !== null && unit_price !== undefined
            ? `${formatCurrency(unit_price, { currency, locale })} / ${unit_measurement}`
            : '-';

    const { refs, floatingStyles, context, strategy, x, y } = useFloating({
        open,
        onOpenChange: setOpen,
        whileElementsMounted: autoUpdate,
        placement: 'bottom-end',
        middleware: [
            flip(),
            offset({
                mainAxis: -8,
                crossAxis: -4,
            }),
        ],
    });

    const { getFloatingProps, getReferenceProps } = useInteractions([
        useClick(context),
        useRole(context, { role: 'listbox' }),
        useDismiss(context),
    ]);

    return (
        <div
            className={cn(
                'grid grid-cols-[2fr_1fr_auto] sm:grid-cols-[1.5fr_1fr_1fr_auto] gap-2 items-center py-0.5',
                className,
            )}
        >
            <button
                type="button"
                onClick={() => onEdit(reference)}
                className="pl-3 text-sm flex items-center gap-2 grow min-w-0 text-gray-900"
            >
                <Icon name="supplies" color="currentColor" className="shrink-0" size={20} />
                <div className="font-semibold min-w-0 truncate">{name}</div>
            </button>
            <div className="text-xs truncate text-gray-500">{typeLabel}</div>
            <div className="text-xs truncate text-gray-500 hidden sm:block">{priceLabel}</div>

            <div className="relative flex items-center">
                <IconButton
                    ref={refs.setReference}
                    {...getReferenceProps()}
                    icon={<Icon name="dots-vertical" />}
                    variant="ghost"
                    onClick={() => setOpen(!open)}
                    className={cn(open && 'text-dark-blue')}
                />

                <AnimatePresence>
                    {open && (
                        <FloatingFocusManager context={context}>
                            <motion.div
                                ref={refs.setFloating}
                                style={{
                                    ...floatingStyles,
                                    position: strategy,
                                    top: y ?? 0,
                                    left: x ?? 0,
                                }}
                                className="rounded-md bg-white ring ring-black/5 shadow-sm z-dropdown min-w-[100px]"
                                {...getFloatingProps()}
                                initial={{ opacity: 0, y: -8 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -8 }}
                                transition={{ duration: 0.15, ease: 'easeOut' }}
                            >
                                <div className="p-1 grid">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            onEdit(reference);
                                            setOpen(false);
                                        }}
                                        className="text-sm px-2 py-1.5 rounded-sm transition-colors text-left font-medium hover:bg-gray-100"
                                    >
                                        {t('edit')}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            onDelete(reference);
                                            setOpen(false);
                                        }}
                                        className="text-sm px-2 py-1.5 rounded-sm transition-colors text-left font-medium hover:bg-gray-100 text-danger"
                                    >
                                        {t('delete')}
                                    </button>
                                </div>
                            </motion.div>
                        </FloatingFocusManager>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
}

ReferenceRow.propTypes = {
    reference: PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        type: PropTypes.string,
        unit_price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        unit_measurement: PropTypes.string,
    }).isRequired,
    onEdit: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    className: PropTypes.string,
};

export default function InvoicesInvoicingReferencesDrawer() {
    const { data, isLoading, isError, isFetching, hasNextPage, fetchNextPage } = useInfiniteReferences();
    const { open } = useDrawerStack();
    const { t } = useTranslation('invoices');

    const queryClient = useQueryClient();

    const references =
        data?.pages?.reduce((acc, page) => {
            return [...acc, ...(page.items ?? [])];
        }, []) ?? [];

    const handleAddReference = () => {
        open(DRAWER_STACK.REFERENCES_EDITOR, {
            title: t('new-reference'),
            reference: {},
            onSubmit: async (payload) => {
                await createReference(payload);
                await queryClient.invalidateQueries({ queryKey: ['references'] });
            },
        });
    };

    const handleEditReference = (reference) => {
        open(DRAWER_STACK.REFERENCES_EDITOR, {
            title: t('edit-reference'),
            reference,
            onSubmit: async (payload) => {
                await updateReference(reference.id, payload);
                await queryClient.invalidateQueries({ queryKey: ['references'] });
            },
        });
    };

    const handleDeleteReference = (reference) => {
        open(DRAWER_STACK.CONFIRMATION_DIALOG, {
            title: t('delete-reference'),
            description: t('delete-reference-description', { name: reference.name }),
            onSubmit: async () => {
                await deleteReference(reference.id);
                await queryClient.invalidateQueries({ queryKey: ['references'] });
            },
        });
    };

    return (
        <div className="py-4">
            <div className="rounded-lg border border-gray-200 bg-white">
                {isLoading && (
                    <div className="py-4 flex justify-center">
                        <Loading />
                    </div>
                )}

                {isError && (
                    <div className="py-9 px-2 text-center text-sm text-gray-500 border-b border-gray-200">
                        {t('error-loading-references')}
                    </div>
                )}

                {!isLoading && references.length === 0 && (
                    <div className="py-9 px-2 text-center text-sm text-gray-500 border-b border-gray-200">
                        {t('no-references')}
                    </div>
                )}

                {!isLoading && references.length > 0 && (
                    <div className="grid">
                        {references.map((reference) => (
                            <ReferenceRow
                                className="border-b border-gray-200"
                                key={reference.id}
                                reference={reference}
                                onEdit={() => handleEditReference(reference)}
                                onDelete={() => handleDeleteReference(reference)}
                            />
                        ))}
                    </div>
                )}

                <div className="py-0.5">
                    <Button variant="ghost" className="text-sm px-3" onClick={handleAddReference}>
                        <OutlineAdd />
                        {t('add-reference')}
                    </Button>
                </div>
            </div>

            <InfiniteScroll
                hasNextPage={hasNextPage}
                isFetching={isFetching}
                fetchNextPage={fetchNextPage}
                className="mt-4"
            />
        </div>
    );
}

